import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useGameStore } from './gameStore';

// Local-storage keys that hold user-scoped state but are not user-keyed.
// Clearing these on logout / new-user-login prevents data bleed between accounts on the same device.
const UNSCOPED_LOCAL_KEYS = [
  'dmp_weather_location',          // zipcode → caused Tampa, Florida to follow other users
  'trial_banner_dismissed_date',
  'whats_new_dismissed',
  'install_prompt_dismissed_at',
  'sarcasm_quips',
  'token',                         // legacy plain-token storage (some older code paths look here)
  'ds_promo_v1',                   // captured promo from URL (intentional one-time-use)
];

function clearUserScopedLocalState() {
  try {
    UNSCOPED_LOCAL_KEYS.forEach((k) => {
      try { window.localStorage.removeItem(k); } catch {}
    });
  } catch {}
  // Wipe the persisted gameStore (teams/games/seasons/currentTeamId/etc.)
  try { useGameStore.getState().resetStore(); } catch {}
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isCoach: false,
      teamsAsCoach: [],
      subscriptionTier: null,
      subscriptionActive: false,
      isAdmin: false,
      registeredAs: null,

      setAuth: (user, token) => {
        // If we're switching to a different user (or logging in fresh), clear all
        // unscoped local state so we don't render the previous user's data.
        const prevId = get().user?.id;
        if (!prevId || prevId !== user?.id) {
          clearUserScopedLocalState();
        }
        set({
          user,
          token,
          isAuthenticated: true,
          isCoach: user?.is_coach || false,
          teamsAsCoach: user?.teams_as_coach || [],
          subscriptionTier: user?.subscription_tier || null,
          subscriptionActive: user?.subscription_active || false,
          isAdmin: user?.is_admin || false,
          registeredAs: user?.registered_as || null
        });
      },

      updateCoachStatus: (isCoach, teamsAsCoach) => {
        set({ isCoach, teamsAsCoach });
      },

      updateSubscription: (tier, active) => {
        set({
          subscriptionTier: tier,
          subscriptionActive: active
        });
      },

      // Check if user has access to a feature
      hasFeatureAccess: (feature) => {
        const state = get();
        // Admins have access to everything
        if (state.isAdmin) return true;

        const proFeatures = ['spray_charts', 'advanced_stats', 'multiple_teams'];

        if (proFeatures.includes(feature)) {
          return state.subscriptionActive && state.subscriptionTier === 'pro';
        }

        return true; // Free features
      },

      getMaxTeams: () => {
        const state = get();
        if (state.isAdmin) return 999;
        if (!state.subscriptionActive) return 1;
        if (state.subscriptionTier === 'pro') return 3;
        return 1; // Coach tier
      },

      logout: () => {
        // Always clear unscoped local state on logout — next user gets a clean slate.
        clearUserScopedLocalState();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isCoach: false,
          teamsAsCoach: [],
          subscriptionTier: null,
          subscriptionActive: false,
          isAdmin: false,
          registeredAs: null
        });
      },

      getAuthHeader: () => {
        const state = get();
        return state.token ? { Authorization: `Bearer ${state.token}` } : {};
      }
    }),
    {
      name: 'diamond-manager-auth',
      version: 4
    }
  )
);
